import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { config } from '../../config';
import { isValidEmail, debounce } from '../../utils/helpers';
import { Close, PersonAdd } from '../icons';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import MemberOnlineStatus from '../Global/MemberOnlineStatus';
import ZoobbeSelect from '../Global/ZoobbeSelect';
import Spinner from '../Global/Spinner';
import { setPagePublicStatus } from '../../redux/Slices/pageSlice';
import './scss/SharePageMembers.scss';

const SharePageMembers = ({ page: propPage, isOwner: propIsOwner }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);


  // Get reactive page from Redux store using the shortId from propPage
  const pageId = propPage?.shortId;
  const page = useSelector(state => state.pages.byId[pageId]) || propPage;

  // Re-derive isOwner to ensure it's reactive
  const isOwner = propIsOwner || page?.role === 'owner';

  const [activeTab, setActiveTab] = useState('share'); // 'share' or 'publish'
  const [email, setEmail] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  // Local members state. Mutations commit from the server response (not
  // instant-optimistic): we show a per-member pending state during the request
  // so a failed role-change/remove never flashes a change that didn't happen.
  const [members, setMembers] = useState([]);
  const [pendingMemberId, setPendingMemberId] = useState(null);

  // Publish state
  const [publishLoading, setPublishLoading] = useState(false);

  const emailInputRef = useRef(null);
  const emailRef = useRef();



  // Fetch members when component mounts
  useEffect(() => {
    const fetchMembers = async () => {
      if (!page?.shortId) return;

      setLoadingMembers(true);
      try {
        const response = await fetch(`${config.API_URI}/v1/pages/${page.shortId}/members`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setMembers(data.members || []);
        }
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [page?.shortId]);

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  // Debounced search function
  const searchUsers = useRef(
    debounce(async (searchQuery) => {
      if (!searchQuery || searchQuery.trim().length === 0) {
        setSearchResults([]);
        setSearchingUsers(false);
        return;
      }

      setSearchingUsers(true);
      try {
        const response = await fetch(
          `${config.API_URI}/v1/users/all-users?search=${encodeURIComponent(searchQuery)}&limit=10`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const users = data.users || [];

          if (users.length === 0 && isValidEmail(searchQuery)) {
            setSearchResults([{ email: searchQuery, isEmailInvite: true }]);
          } else {
            setSearchResults(users);
          }
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setSearchingUsers(false);
      }
    }, 300)
  ).current;
 
  // EARLY RETURN AFTER HOOKS
  if (!page) return null;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    searchUsers(value);
  };

  const addUser = (user) => {
    // Prevent adding users who already have access (matches Notion behavior)
    const alreadyMember = members.some((m) => {
      const memberId = m.user?._id;
      const memberEmail = m.user?.email;
      return (
        (user._id && memberId && user._id === memberId) ||
        (user.email && memberEmail && user.email === memberEmail)
      );
    });

    if (alreadyMember) {
      showError(dispatch, 'This person already has access to the page');
      setEmail('');
      setSearchResults([]);
      return;
    }

    if (!selectedUsers.find(u => u._id === user._id || u.email === user.email)) {
      setSelectedUsers([...selectedUsers, user]);
    }
    setEmail('');
    setSearchResults([]);
  };

  const removeUser = (index) => {
    setSelectedUsers(selectedUsers.filter((_, i) => i !== index));
  };

  const handleSharePage = async (e) => {
    e.preventDefault();

    if (selectedUsers.length === 0) {
      showError(dispatch, 'Please select at least one user');
      return;
    }

    setLoading(true);

    // Build optimistic members to show immediately
    const optimisticNewMembers = selectedUsers.map((sel) => ({
      user: {
        _id: sel._id || undefined,
        name: sel.name || sel.username || sel.email,
        username: sel.username,
        email: sel.email || (sel.isEmailInvite ? sel.email : undefined),
        profilePicture: sel.profilePicture || null,
      },
      role: selectedRole || 'viewer',
      _optimistic: true,
    }));

    const prevMembers = members;
    setMembers([...members, ...optimisticNewMembers]);

    try {
      const payload = {
        userIdToAdd: selectedUsers[0]._id || null,
        email: selectedUsers[0].email || null,
        role: selectedRole || 'viewer',
      };

      const response = await fetch(`${config.API_URI}/v1/pages/${page.shortId}/members`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        // Update members list with the response from backend
        if (data.members) {
          setMembers(data.members);
        }
        showSuccess(dispatch, data.message || 'Member added successfully');
        setSelectedUsers([]);
        setEmail('');
      } else {
        // Revert UI
        setMembers(prevMembers);
        showError(dispatch, data.message || 'Failed to add member');
      }
    } catch (error) {
      // Revert UI
      setMembers(prevMembers);
      console.error('Error sharing page:', error);
      showError(dispatch, 'Failed to share page');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'editor', label: 'Can edit', description: 'Edit, suggest, and comment' },
    { value: 'commenter', label: 'Can comment', description: 'Suggest and comment' },
    { value: 'viewer', label: 'Can view', description: 'View only' },
  ];

  // Role options for existing members (includes owner and remove)
  const getMemberRoleOptions = (member) => {
    const options = [
      { value: 'owner', label: 'Full access', description: 'Edit, suggest, comment, and share with others' },
      { value: 'editor', label: 'Can edit', description: 'Edit, suggest, and comment' },
      { value: 'commenter', label: 'Can comment', description: 'Suggest and comment' },
      { value: 'viewer', label: 'Can view', description: 'View only' },
    ];

    // Add remove option if owner
    if (isOwner && member.role !== 'owner') {
      // Keep “Remove access” as a single, clean line (no extra description)
      options.push({ value: 'remove', label: 'Remove access', isDanger: true });
    }

    return options;
  };

  const handleRoleChange = async (member, newRole) => {
    if (newRole === 'remove') {
      await handleRemoveMember(member);
      return;
    }

    if (!isOwner) {
      showError(dispatch, 'Only the owner can change member roles');
      return;
    }

    // Show this member as pending and commit only from the server response —
    // no instant optimistic change that could flash and revert.
    setPendingMemberId(member.user._id);

    try {
      const response = await fetch(`${config.API_URI}/v1/pages/${page.shortId}/members/${member.user._id}`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        showSuccess(dispatch, data.message || 'Role updated successfully');
        // Commit from the server: use the returned list, or apply the change locally.
        if (data.members) {
          setMembers(data.members);
        } else {
          setMembers(prev => prev.map(m =>
            m.user?._id === member.user?._id ? { ...m, role: newRole } : m
          ));
        }
      } else {
        showError(dispatch, data.message || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      showError(dispatch, 'Failed to update role');
    } finally {
      setPendingMemberId(null);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!isOwner) {
      showError(dispatch, 'Only the owner can remove members');
      return;
    }

    if (!window.confirm(`Remove ${member.user?.name || member.user?.email} from this page?`)) {
      return;
    }

    // Show this member as pending and remove only after the server confirms —
    // avoids the jarring "flash of removal then it comes back" on failure.
    setPendingMemberId(member.user._id);

    try {
      const response = await fetch(`${config.API_URI}/v1/pages/${page.shortId}/members/${member.user._id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        showSuccess(dispatch, data.message || 'Member removed successfully');
        // Commit from the server: use the returned list, or remove locally.
        if (data.members) {
          setMembers(data.members);
        } else {
          setMembers(prev => prev.filter(m => m.user?._id !== member.user?._id));
        }
      } else {
        showError(dispatch, data.message || 'Failed to remove member');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      showError(dispatch, 'Failed to remove member');
    } finally {
      setPendingMemberId(null);
    }
  };

  const handlePublishToggle = async () => {
    if (!isOwner) {
      showError(dispatch, 'Only the owner can publish pages');
      return;
    }

    setPublishLoading(true);
    const isPublished = page?.isPublic || false;
    const newPublishState = !isPublished;

    try {
      // Use different endpoints for enable/disable
      const endpoint = newPublishState
        ? `${config.API_URI}/v1/pages/${page.shortId}/share/public`
        : `${config.API_URI}/v1/pages/${page.shortId}/share/revoke`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        if (newPublishState && data.publicShareToken) {
          dispatch(setPagePublicStatus({ pageId: page.shortId, isPublic: true, publicShareToken: data.publicShareToken }));
          showSuccess(dispatch, data.message || 'Page published successfully');
        } else {
          dispatch(setPagePublicStatus({ pageId: page.shortId, isPublic: false, publicShareToken: null }));
          showSuccess(dispatch, data.message || 'Page unpublished successfully');
        }
      } else {
        showError(dispatch, data.message || 'Failed to update publish status');
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
      showError(dispatch, 'Failed to update publish status');
    } finally {
      setPublishLoading(false);
    }
  };

  const isPublished = page?.isPublic || false;
  const publicLink = page?.publicShareToken ? `${window.location.origin}/share/${page.shortId}?token=${page.publicShareToken}` : '';

  const handleCopyLink = () => {
    if (publicLink) {
      navigator.clipboard.writeText(publicLink);
      showSuccess(dispatch, 'Link copied to clipboard');
    }
  };

  return (
    <div className="share-page-members">
      <div className="spm-header">
        <h3>Share "{page.title || 'Untitled'}"</h3>
      </div>

      {/* Tabs */}
      <div className="spm-tabs">
        <button
          className={`spm-tab ${activeTab === 'share' ? 'active' : ''}`}
          onClick={() => setActiveTab('share')}
        >
          Share
        </button>
        <button
          className={`spm-tab ${activeTab === 'publish' ? 'active' : ''}`}
          onClick={() => setActiveTab('publish')}
        >
          Publish
        </button>
      </div>

      {/* Share Tab Content */}
      {activeTab === 'share' && (
        <>
          <form onSubmit={handleSharePage}>
        <div className="spm-input-group">
          <div
            className="spm-email-input"
            ref={emailRef}
            style={{
              outline: isFocused ? '1px solid var(--brand-color)' : '1px solid var(--input-border-color)',
            }}
          >
            {selectedUsers.map((selectedUser, index) => (
              <div key={index} className="spm-selected-email">
                {`${selectedUser.name || selectedUser.email}`}
                <button type="button" onClick={() => removeUser(index)}>
                  <Close size={16} />
                </button>
              </div>
            ))}
            <input
              ref={emailInputRef}
              type="text"
              placeholder="Add people by name or email"
              value={email}
              onChange={handleEmailChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => setIsFocused(false), 200)}
            />
          </div>

          <div className="spm-role-select">
            <ZoobbeSelect
              options={roleOptions}
              defaultSelectedOption={2}
              onSelect={(option) => setSelectedRole(option.value)}
            />
          </div>

          <button
            type="submit"
            className="spm-share-btn"
            disabled={loading || selectedUsers.length === 0}
          >
            {loading ? <Spinner size={16} /> : <PersonAdd size={18} />}
            {loading ? 'Sharing...' : 'Share'}
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="spm-search-results">
            {searchingUsers ? (
              <div className="spm-searching">
                <Spinner size={20} />
                <span>Searching...</span>
              </div>
            ) : (
              searchResults.map((result, index) => (
                <div
                  key={index}
                  className="spm-search-result-item"
                  onClick={() => addUser(result)}
                >
                  {result.isEmailInvite ? (
                    <>
                      <div className="spm-result-avatar">
                        <span className="material-symbols-outlined">mail</span>
                      </div>
                      <div className="spm-result-info">
                        <div className="spm-result-name">Invite {result.email}</div>
                        <div className="spm-result-email">Send invitation via email</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="spm-result-avatar">
                        {result.profilePicture ? (
                          <img src={result.profilePicture} alt={result.name} loading="lazy" />
                        ) : (
                          <ImagePlaceholder text={result.username || result.name} size={32} onlineIndicator={false} />
                        )}
                      </div>
                      <div className="spm-result-info">
                        <div className="spm-result-name">{result.username || result.name}</div>
                        <div className="spm-result-email">{result.email}</div>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        )}
          </form>

          {/* Current Members */}
          <div className="spm-current-members">
          <h4>People with access</h4>
          {loadingMembers ? (
            <div className="spm-loading-members">
              <Spinner size={20} strokeWidth={7} />
            </div>
          ) : members && members.length > 0 ? (
            <div className="spm-members-list">
              {members.map((member, index) => {
                const currentRoleOption = getMemberRoleOptions(member).find(opt => opt.value === member.role);
                const isCurrentUser = member.user?._id === user?.user?.id || member.user?.email === user?.user?.email;
                // Use user._id as key, fallback to email or index
                const memberKey = member.user?._id || member.user?.email || `member-${index}`;
                const isMemberPending = pendingMemberId && member.user?._id === pendingMemberId;

                return (
                  <div key={memberKey} className={`spm-member-item${isMemberPending ? ' is-pending' : ''}`}>
                    <div className="spm-member-avatar">
                      <div className="spm-member-avatar-img-wrapper">
                        {member.user?.profilePicture ? (
                          <img src={member.user.profilePicture} alt={member.user?.name || 'Member'} loading="lazy" />
                        ) : (
                          <ImagePlaceholder text={member.user?.username || member.user?.name || member.user?.email} size={32} onlineIndicator={false} />
                        )}
                      </div>
                      <MemberOnlineStatus member={member.user} />
                    </div>
                    <div className="spm-member-info">
                      <div className="spm-member-name">
                        {member.user?.username || member.user?.name || member.user?.email}
                        {isCurrentUser && ' (You)'}
                        {member._optimistic && ' (pending)'}
                      </div>
                      <div className="spm-member-email">{member.user?.email}</div>
                    </div>
                    <div className="spm-member-role-dropdown">
                      {isMemberPending ? (
                        <Spinner size={16} strokeWidth={7} />
                      ) : member.role === 'owner' && isCurrentUser ? (
                        <div className="spm-member-role-static">{currentRoleOption?.label || 'Owner'}</div>
                      ) : (
                        <ZoobbeSelect
                          options={getMemberRoleOptions(member)}
                          value={member.role}
                          defaultSelectedOption={getMemberRoleOptions(member).findIndex(opt => opt.value === member.role)}
                          onSelect={(option) => handleRoleChange(member, option.value)}
                          showDescription={true}
                          disabled={!isOwner || member._optimistic || !!pendingMemberId}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="spm-no-members">
              <p>No additional members yet. Share this page to collaborate!</p>
            </div>
          )}
          </div>
        </>
      )}

      {/* Publish Tab Content */}
      {activeTab === 'publish' && (
        <div className="spm-publish-section">
          <div className="spm-publish-toggle">
            <div className="spm-publish-info">
              <h4>Publish to web</h4>
              <p>Publish and share link with anyone</p>
            </div>
            <label className="spm-toggle-switch">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={handlePublishToggle}
                disabled={publishLoading || !isOwner}
              />
              <span className="spm-toggle-slider"></span>
            </label>
          </div>

          {isPublished && publicLink && (
            <div className="spm-public-link-section">
              <div className="spm-link-display">
                <input
                  type="text"
                  value={publicLink}
                  readOnly
                  className="spm-link-input"
                />
                <button
                  type="button"
                  className="spm-copy-btn"
                  onClick={handleCopyLink}
                >
                  Copy link
                </button>
              </div>
              <p className="spm-link-description">
                Anyone with this link can view this page
              </p>
            </div>
          )}

          {!isPublished && (
            <div className="spm-publish-description">
              <p>
                When you publish this page, anyone on the internet with the link will be able to view it.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SharePageMembers;

